<template>
  <div class="row mb-2 mb-xl-3">
    <div class="col-auto d-block">
      <h4 class="text-dark">Forecast Set</h4>
    </div>
    <div class="col-auto ml-auto text-right mt-n1">
      <button
        type="button"
        class="btn btn-primary"
        @click="saveChanges"
        :disabled="loading"
      >
        <i class="bi bi-check2-square mr-1"></i> Guardar
      </button>
    </div>
  </div>

  <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
    <div class="d-flex align-items-center gap-2">
      <span class="text-muted small">Mostrar</span>
      <select class="form-control form-control-sm d-inline-block w-auto" v-model.number="pageSize">
        <option :value="10">10</option>
        <option :value="25">25</option>
        <option :value="50">50</option>
        <option :value="100">100</option>
      </select>
      <span class="text-muted small">entradas</span>
    </div>
    <div class="d-flex align-items-center gap-2">
      <span class="text-muted small">Buscar:</span>
      <input
        type="text"
        class="form-control form-control-sm d-inline-block w-auto"
        v-model="searchQuery"
        @input="currentPage = 1"
        placeholder=""
      />
    </div>
  </div>

  <table class="table table-sm table-striped bg-white" style="width:100%">
    <thead class="thead-dark">
      <tr>
        <th class="font-weight-normal">Nombre</th>
        <th class="font-weight-normal text-center checkbox-column">Incluir</th>
      </tr>
    </thead>
    <tbody>
      <tr v-if="loading">
        <td colspan="2" class="text-center text-muted py-3">Cargando...</td>
      </tr>
      <tr v-else-if="pagedLines.length === 0">
        <td colspan="2" class="text-center text-muted py-3">
          {{ searchQuery ? 'No se encontraron resultados' : 'Sin líneas de producción' }}
        </td>
      </tr>
      <tr v-for="line in pagedLines" :key="line.ID">
        <td class="font-weight-light align-middle">{{ line.Nombre }}</td>
        <td class="text-center align-middle">
          <input
            type="checkbox"
            :checked="line.checked"
            @change="toggleLine(line.ID)"
          />
        </td>
      </tr>
    </tbody>
  </table>

  <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
    <div class="text-muted small">
      <template v-if="filteredLines.length > 0">
        Mostrando {{ rangeStart }} a {{ rangeEnd }} de {{ filteredLines.length }} líneas
        <template v-if="searchQuery">(filtrado de {{ productionLines.length }} total)</template>
      </template>
      <template v-else>No se encontraron resultados</template>
    </div>
    <nav v-if="totalPages > 1">
      <ul class="pagination pagination-sm mb-0">
        <li class="page-item" :class="{ disabled: currentPage === 1 }">
          <button class="page-link" @click="currentPage--" :disabled="currentPage === 1">Previo</button>
        </li>
        <li
          v-for="page in visiblePages"
          :key="page"
          class="page-item"
          :class="{ active: page === currentPage, disabled: page === '…' }"
        >
          <button class="page-link" @click="page !== '…' && (currentPage = page)">{{ page }}</button>
        </li>
        <li class="page-item" :class="{ disabled: currentPage === totalPages }">
          <button class="page-link" @click="currentPage++" :disabled="currentPage === totalPages">Siguiente</button>
        </li>
      </ul>
    </nav>
  </div>

  <toast :config="alert"></toast>
</template>

<script>
import actions from "@/util/actions";
import { onMounted, reactive, ref, computed } from "vue";
import bufferApi from "../api/bufferApi";
import $ from 'jquery'

export default {
  setup() {
    const loading = ref(false);
    const forecastIds = ref(new Set());
    const productionLines = ref([]);
    const searchQuery = ref("");
    const currentPage = ref(1);
    const pageSize = ref(10);
    const alert = reactive({ message: "", title: "", variant: "" });

    // Líneas filtradas por búsqueda
    const filteredLines = computed(() => {
      const q = searchQuery.value.trim().toLowerCase();
      if (!q) return productionLines.value;
      return productionLines.value.filter((l) =>
        l.Nombre.toLowerCase().includes(q)
      );
    });

    const totalPages = computed(() =>
      Math.max(1, Math.ceil(filteredLines.value.length / pageSize.value))
    );

    const pagedLines = computed(() => {
      const start = (currentPage.value - 1) * pageSize.value;
      return filteredLines.value.slice(start, start + pageSize.value);
    });

    const rangeStart = computed(() =>
      filteredLines.value.length === 0
        ? 0
        : (currentPage.value - 1) * pageSize.value + 1
    );

    const rangeEnd = computed(() =>
      Math.min(currentPage.value * pageSize.value, filteredLines.value.length)
    );

    const visiblePages = computed(() => {
      const total = totalPages.value;
      const cur = currentPage.value;
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
      const pages = [];
      pages.push(1);
      if (cur > 3) pages.push("…");
      for (let p = Math.max(2, cur - 1); p <= Math.min(total - 1, cur + 1); p++) {
        pages.push(p);
      }
      if (cur < total - 2) pages.push("…");
      pages.push(total);
      return pages;
    });

    onMounted(initTable);

    async function initTable() {
      loading.value = true;
      try {
        const [{ data: linesData }, { data: forecastData }] = await Promise.all([
          bufferApi.get("lineas_produccion"),
          bufferApi.get("forecast_sets"),
        ]);

        const lines = linesData.result ?? [];
        const forecastLines = forecastData.result ?? [];
        forecastIds.value = new Set(forecastLines.map((l) => l.ID));
        const forecastLineIds = new Set(forecastLines.map((l) => l.LineaProduccionID));

        productionLines.value = lines.map((line) => ({
          ...line,
          checked: forecastLineIds.has(line.ID),
        }));

        currentPage.value = 1;
      } catch (error) {
        if (error.response?.status === 401) {
          actions.logout();
          return;
        }
        alert.title = "Error al buscar";
        alert.message = error.response?.data?.msg ?? "Error en la petición";
        alert.variant = "danger";
        $('#toast').toast('show');
      } finally {
        loading.value = false;
      }
    }

    function toggleLine(lineId) {
      const line = productionLines.value.find((l) => l.ID === lineId);
      if (line) line.checked = !line.checked;
    }

    async function saveChanges() {
      loading.value = true;
      try {
        await bufferApi.delete("eliminar_forecast_set_masivo", {
          data: { ids: [...forecastIds.value] },
        });

        const selectedLineIds = productionLines.value
          .filter((l) => l.checked)
          .map((l) => l.ID);

        if (!selectedLineIds.length) {
          alert.title = "Guardado";
          alert.message = "No se seleccionaron líneas para guardar";
          alert.variant = "warning";
          $('#toast').toast('show');
          return;
        }

        await bufferApi.post("forecast_set_masivo", {
          lineas_produccion_id: selectedLineIds,
        });

        alert.title = "Guardado";
        alert.message = "Cambios de forecast guardados correctamente";
        alert.variant = "success";
        $('#toast').toast('show');

        await initTable();
      } catch (error) {
        if (error.response?.status === 401) {
          actions.logout();
          return;
        }
        alert.title = "Error al guardar";
        alert.message = error.response?.data?.msg ?? "Error al guardar los cambios";
        alert.variant = "danger";
        $('#toast').toast('show');
      } finally {
        loading.value = false;
      }
    }

    return {
      loading,
      alert,
      productionLines,
      searchQuery,
      currentPage,
      pageSize,
      filteredLines,
      pagedLines,
      totalPages,
      visiblePages,
      rangeStart,
      rangeEnd,
      toggleLine,
      saveChanges,
    };
  },
};
</script>

<style lang="scss">
.checkbox-column {
  width: 140px;
}

.gap-2 {
  gap: 0.5rem;
}
</style>